'use client';

import { isPinakesConfigured } from '@/config/pinakes';
import { PinakesController } from '@/controllers/pinakes/pinakes';
import { usePinakesQuery } from '@/hooks/usePinakesQuery/usePinakesQuery';
import type { UsePinakesQueryResult } from '@/hooks/usePinakesQuery/usePinakesQuery.types';
import type { TPinakesResourceDetail } from '@/services/pinakes/pinakes.types';

/**
 * The Pinakes card for a link.
 *
 * `data` is `null` when there is no card to show: Pinakes does not know the link (404), Pinakes is
 * not configured (`PUBKY_RUNTIME_PINAKES_URL` unset, no request is made), or `uri` is empty.
 * Callers keep their plain link preview then.
 *
 * @param uri - The link as written in the post
 *
 * @example
 * ```tsx
 * const { data: card, isLoading } = usePinakesCard(url);
 * if (card === null) return <GenericPreview url={url} />;
 * ```
 */
export function usePinakesCard(uri: string | null | undefined): UsePinakesQueryResult<TPinakesResourceDetail> {
  return usePinakesQuery({
    key: uri ?? '',
    fetchFn: () => PinakesController.fetchCardByUri({ uri: uri! }),
    enabled: Boolean(uri) && isPinakesConfigured(),
  });
}
